import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  LogOut,
  Mail,
  ShoppingBag,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import StatsPanel from '../components/admin/StatsPanel';
import ContactsList from '../components/admin/ContactsList';
import OrdersList from '../components/admin/OrdersList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TabButton = ({ active, onClick, icon: Icon, children, testId }) => (
  <Button
    onClick={onClick}
    className={active ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}
    data-testid={testId}
  >
    <Icon className="h-4 w-4 mr-2" />
    {children}
  </Button>
);

const AdminDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, contactsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/contacts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/orders`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data.contacts || []);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchDashboardData();
    }
  }, [user, authLoading, navigate, fetchDashboardData]);

  const handleDeleteContact = useCallback(async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/contacts/${contactId}`, {
        withCredentials: true,
      });
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    }
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" data-testid="admin-dashboard">
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome back, {user?.name}</p>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex gap-2 overflow-x-auto">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            icon={TrendingUp}
            testId="tab-overview"
          >
            Overview
          </TabButton>
          <TabButton
            active={activeTab === 'contacts'}
            onClick={() => setActiveTab('contacts')}
            icon={Mail}
            testId="tab-contacts"
          >
            Contacts ({contacts.length})
          </TabButton>
          <TabButton
            active={activeTab === 'orders'}
            onClick={() => setActiveTab('orders')}
            icon={ShoppingBag}
            testId="tab-orders"
          >
            Orders ({orders.length})
          </TabButton>
        </div>

        {activeTab === 'overview' && <StatsPanel stats={stats} contacts={contacts} />}
        {activeTab === 'contacts' && (
          <ContactsList
            contacts={contacts}
            onRefresh={fetchDashboardData}
            onDelete={handleDeleteContact}
          />
        )}
        {activeTab === 'orders' && <OrdersList orders={orders} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
